<template>
  <section class="products-section flex-col">
    <h2 v-if="showHeading">{{ heading }}</h2>
    <p v-if="showSubHeading" class="subHeading">
      {{ subHeading }}
    </p>

    <!-- Tabs -->
    <div v-if="showTabs" class="tabs-container" role="tablist" aria-label="Live Opportunities Tabs">
      <button
        type="button"
        id="tab-bonds"
        class="tab"
        :class="{ active: activeTab === 'bonds' }"
        role="tab"
        :aria-selected="activeTab === 'bonds'"
        :tabindex="activeTab === 'bonds' ? 0 : -1"
        aria-controls="panel-bonds"
        @click="handleTabChange('bonds')"
        @keydown="handleTabKeydown($event, 'bonds')"
      >
        <span class="tab-label">Bonds</span>
      </button>
      <button
        type="button"
        id="tab-fds"
        class="tab"
        :class="{ active: activeTab === 'fds' }"
        role="tab"
        :aria-selected="activeTab === 'fds'"
        :tabindex="activeTab === 'fds' ? 0 : -1"
        aria-controls="panel-fds"
        @click="handleTabChange('fds')"
        @keydown="handleTabKeydown($event, 'fds')"
      >
        <span class="tab-label">FDs</span>
      </button>
    </div>

    <!-- Chip filters (only for bonds and when tabs are shown) -->
    <div
      v-if="showTabs && activeTab === 'bonds'"
      class="chip-row"
      role="tablist"
      aria-label="Bond opportunity filters"
    >
      <button
        v-for="chip in chips"
        :id="`bond-filter-tab-${chip.key}`"
        :key="chip.key"
        type="button"
        class="chip"
        :class="{ active: selectedChip === chip.key }"
        role="tab"
        :aria-selected="selectedChip === chip.key"
        :tabindex="selectedChip === chip.key ? 0 : -1"
        aria-controls="bond-filter-panel"
        @click="handleChipClick(chip.key)"
        @keydown="handleChipKeydown($event, chip.key)"
      >
        {{ chip.label }}
      </button>
      <!-- <button class="chip view-all" @click="handleViewAll">View All</button> -->
    </div>

    <div class="content">
      <!-- Bonds: Empty State or Cards -->
      <div
        role="tabpanel"
        id="panel-bonds"
        :aria-labelledby="'tab-bonds'"
        v-show="activeTab === 'bonds'"
        :aria-hidden="activeTab !== 'bonds'"
      >
        <div
          id="bond-filter-panel"
          class="bonds-grid"
          role="tabpanel"
          :aria-labelledby="`bond-filter-tab-${selectedChip}`"
        >
          <div v-if="bondsLoading" class="loading-state">
            <div v-for="n in 6" :key="'sk-' + n" class="skeleton" />
          </div>
          <div v-else-if="(!filteredBondsCards || filteredBondsCards.length === 0) && (!ipoCards || ipoCards.length === 0)" class="empty-state">
            <div class="empty-state-icon">📊</div>
            <h3 class="empty-state-title">No bonds available</h3>
            <p class="empty-state-message">
              {{ (!bondsWithMetadata.value || bondsWithMetadata.value.length === 0)
                ? 'We\'re currently updating our bond listings. Please check back soon!'
                : 'No bonds match your selected filter. Try selecting a different option or view all bonds.' }}
            </p>
            <!-- <button v-if="selectedChip !== 'all'" class="empty-state-button" @click="handleChipClick('all')">
            View All Bonds
          </button> -->
          </div>

          <template v-else>
            <IPOCard v-for="ipo in ipoCards" :key="`ipo-${ipo.id}`" v-bind="ipo" @view-apply="onIpoClick(ipo)"
              @card-click="onIpoClick(ipo)" />
            <BondProductCard v-for="item in bondsWithMetadata" :key="item.id || index" v-bind="item.card"
              :bond-id="item.segmentId" @knowMore="onKnowMore(item)" @cardClick="onCardClick(item)" />
          </template>
        </div>
      </div>

      <!-- FDs: Empty State or Cards -->
      <div
        role="tabpanel"
        id="panel-fds"
        aria-labelledby="tab-fds"
        v-show="activeTab === 'fds'"
        :aria-hidden="activeTab !== 'fds'"
      >
        <div v-if="!financeCards || financeCards.length === 0" class="empty-state">
          <div class="empty-state-icon">📊</div>
          <h3 class="empty-state-title">No fixed deposits available</h3>
          <p class="empty-state-message">
            We're currently updating our FD listings. Please check back soon!
          </p>
        </div>

        <div class="bonds-grid" v-else>
          <CommonFinanceCard v-for="(card, index) in financeCards" :key="index" v-bind="card" />
        </div>
      </div>
    </div>

    <!-- Explore All Bonds CTA -->
    <div v-if="showTabs && activeTab === 'bonds' && filteredBondsCards && filteredBondsCards.length > 0"
      class="cta-section">
      <div class="cta-content">
        <CommonButton text="Explore all bonds" :color="'#0027AF'" width="220px" height="48px" fontSize="15px"
          to="/products/explore" class="cta-button" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import { useTheme } from "~/composables/useTheme";
import bondsService from "~/service/bondsService";
import ipoService from "~/service/ipoService";
import { getBondDetailsUrl, getBondMinInvestmentAmount, getIpoDetailsUrl } from "~/utils/common";
import { normalizeBondListing } from "~/utils/normalizations";

const BondProductCard = defineAsyncComponent(async () => {
  const mod = await import("~/components/bonds/BondProductCard.vue");
  return mod.default || mod;
});
const IPOCard = defineAsyncComponent(async () => {
  const mod = await import("~/components/bonds/IPOCard.vue");
  return mod.default || mod;
});

const { themeName } = useTheme();
const { investorUrl } = useInvestorPlatform();

const props = defineProps({
  heading: { type: String, default: "Featured Opportunities" },
  showHeading: { type: Boolean, default: true },
  subHeading: { type: String, default: "Explore live listings. Review each issuer's rating, terms and documents before you invest." },
  showSubHeading: { type: Boolean, default: true },
  showTabs: { type: Boolean, default: true }
});

const activeTab = ref(props.showTabs ? "bonds" : "fds");
const financeCards = ref([]);
const bondsCards = ref([]);
const ipoCards = ref([]);
const rawFdSources = ref([]);
const rawBondsListings = ref([]);
const bondsWithMetadata = ref([]); // Store bonds with numeric values for filtering

// Chips for filtering bonds
const chips = [
  { key: "min10k", label: "Up to 10K" },
  { key: "highYield", label: "High Yield" },
  // { key: "topDeals", label: "Top Deals" },
  { key: "topRated", label: "Top Rated" },
  { key: "monthlyIncome", label: "Monthly Income" },
  { key: "newIssue", label: "New Issue" },
];

const chipOrder = chips.map((chip) => chip.key);
const selectedChip = ref("highYield");

// Computed properties
const bondsCount = computed(() => bondsWithMetadata.value?.length || 0);
const fdsCount = computed(() => financeCards.value?.length || 0);

// Displayed bonds (fetched from API based on selected chip)
const filteredBondsCards = ref([]);
const bondsLoading = ref(false);

// map local assets by fCode
const localAssets = {
  SHRI: {
    // logo: "/icons/shriram.svg",
    banner: "/images/fdBanners/Shriram",
  },
  USFB: {
    // logo: "/icons/unity.svg",
    banner: "/images/fdBanners/Unity",
  },
  BAJ: {
    // logo: "/icons/bajaj.svg",
    banner: "/images/fdBanners/Bajaj",
  },
  SSFB: {
    // logo: "/icons/suryoday.svg",
    banner: "/images/fdBanners/Suryoday",
  },
  MAH: {
    // logo: "/icons/mahindra.svg",
    banner: "/images/fdBanners/MahindraFinance",
  },
};

// helper: map API data → card props
const mapToFinanceCards = (sources, isDark) => {
  // 🔹 Step 1: Sort by highest interest rate (descending)
  const sortedSources = [...sources].sort((a, b) => {
    const rateA = parseFloat(a.maxInterest || 0);
    const rateB = parseFloat(b.maxInterest || 0);
    return rateB - rateA; // highest first
  });

  // 🔹 Step 2: Map to card objects
  return sortedSources.map((src) => {
    const asset = localAssets[src.fCode] || {};
    const backgroundImage = isDark
      ? `${asset.banner || "/images/fdBanners/default"}Dark.webp`
      : `${asset.banner || "/images/fdBanners/default"}.webp`;

    const formatTenure = (days) => {
      if (days < 365) return `${days}D`;
      const years = Math.round(days / 365);
      return `${years}Y`;
    };

    const tenureText =
      src.minTenure && src.maxTenure
        ? `${formatTenure(src.minTenure)} - ${formatTenure(src.maxTenure)}`
        : "N/A";

    const displayIssuerType =
      src.issuerType === "SMALL FINANCE BANK" ? "SF Bank" : src.issuerType;

    // 🎨 Background colors by fCode
    const getSecondaryFeaturesBg = (fCode) => {
      switch (fCode) {
        case "SHRI": return "#FFF8E1";
        case "USFB": return "#FFF8E1";
        case "BAJ": return "#E3F3FF";
        case "SSFB": return "#FFEDDF";
        case "MAH": return "#FFEAEB";
        default: return "#F5F5F5";
      }
    };

    return {
      title: src.name,
      logo: asset?.logo || src.subLogo,
      badge: src.crisilRating || src.icraRating || "Rated",
      rate: `${src.maxInterest}%`,
      issuerType: displayIssuerType,
      primaryFeatures: [],
      secondaryFeatures: [
        { icon: "mdi-star", isAARated: src.issuerType === "NBFC", crisilRating: src.crisilRating || src.icraRating },
        { minText: `₹${Number(src.minAmount).toLocaleString()}` },
        { tenure: tenureText },
      ],
      secondaryFeaturesBg: getSecondaryFeaturesBg(src.fCode),
      buttonText: "Know More",
      buttonRedirection: src.fIssuerId
        ? `/explore-products?tab=fds&fIssuerId=${encodeURIComponent(src.fIssuerId)}`
        : "/explore-products?tab=fds",
      backgroundImage,
      badgeBgColor: "#FFD700",
      badgeTextColor: "#000",
      earnUptoTextColor: "#FFF",
      rateTextColor: "#FFD700",
      rateUnitColor: "#FFF",
      iconColor: "#FFF",
      featureTextColor: "#FFF",
      buttonBg: "#002FA7",
      buttonTextColor: "#FFF",
      isDicgcInsured: src.issuerType === "SMALL FINANCE BANK",
      DicgcInsuredText:
        src.issuerType === "SMALL FINANCE BANK"
          ? "DICGC Insured Upto ₹5 Lakh"
          : "",
      isSeniorCitizenBenefit: true,
      seniorCitizenBenefitText: `${src.seniorCitizenInterest}% p.a. for Sr. Citizen`,
      isWomenBenefit: src.womenInterest !== "0",
      womenInterestBenefitText: `Additional ${(Number(src.womenInterest) - Number(src.maxInterest)).toFixed(2)}% for Women`
    };
  });
};

// Helper functions for bonds
function formatINR(amount) {
  if (!Number.isFinite(amount)) return "₹0.00";
  return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function badgeFrom(listing) {
  const tags = (listing.tags || []).map((t) => String(t).toLowerCase());
  if (tags.includes("new")) return { label: "New Issuer", variant: "new" };
  return undefined;
}

// Helper to map bonds to BondProductCard props format with metadata for filtering
const mapBondsToCardProps = (listings) => {
  return listings.map((listing) => {
    const y = Number(listing.listedYield || 0);
    const minInv = getBondMinInvestmentAmount(listing);
    // Use segmentId from API response, fallback to id if segmentId is not available
    const segmentId = listing.segmentId || listing.id;

    const card = {
      id: listing.id,
      name: listing.instrument?.name || "—",
      issuerName: listing.instrument?.issuerName || "—",
      issuerLogo: listing.instrument?.logoUrl,
      rating: listing.instrument?.creditRating || "",
      creditRiskMeter: listing.creditRiskMeter || listing.credit_risk_meter || listing.instrument?.creditRiskMeter || listing.instrument?.credit_risk_meter || undefined,
      category: listing.instrument?.category || "",
      yieldRate: y ? y.toFixed(2) : listing.listedYield || "—",
      yieldLabel: "Yield to Maturity",
      minAmount: formatINR(minInv),
      payouts: listing.instrument?.couponFrequency || "—",
      maturityDate: listing.instrument?.maturityDate || "—",
      seniority: listing.instrument?.seniorityRepayment || listing.instrument?.seniority || "",
      issuanceType: listing.instrument?.issuanceType || "",
      badge: badgeFrom(listing),
      tags: listing.tags || [],
      isin: listing.instrument?.isin || "",
    };

    return {
      id: listing.id,
      segmentId: segmentId,
      card,
      raw: listing,
      numericYield: y,
      numericMinInv: minInv,
    };
  });
};

// Map chip key to API quickFilter value
const getQuickFilter = (chipKey) => {
  if (!chipKey || chipKey === "all") return undefined;
  const filterMap = {
    topDeals: "top-deals",
    highYield: "high-yield",
    topRated: "top-rated",
    monthlyIncome: "monthly-income",
    newIssue: "new-issue",
  };
  return filterMap[chipKey];
};

// Get sortBy parameter based on chip key
const getSortBy = (chipKey) => {
  if (chipKey === "topRated") return "credit_rating";
  if (chipKey === "highYield") return "listed_yield";
  if (chipKey === "topDeals") return "listed_yield";
  if (chipKey === "min10k") return "min_investment";
  return undefined;
};

// Get sortOrder parameter based on chip key
const getSortOrder = (chipKey) => {
  if (chipKey === "min10k") return "ASC";
  return undefined;
};

// Get investmentRange parameter based on chip key
const getInvestmentRange = (chipKey) => {
  if (chipKey === "min10k") {
    return [{ maxAmount: 10000, label: "Up to ₹10,000" }];
  }
  return undefined;
};

const fetchBonds = async (chipKey = null) => {
  try {
    bondsLoading.value = true;

    const quickFilter = getQuickFilter(chipKey);
    const sortBy = getSortBy(chipKey);
    const sortOrder = getSortOrder(chipKey);
    const investmentRange = getInvestmentRange(chipKey);

    // Build query parameters
    const params = {
      page: 1,
      limit: 6,
    };

    if (quickFilter) {
      params.quickFilter = quickFilter;
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (sortOrder) {
      params.sortOrder = sortOrder;
    }

    if (investmentRange) {
      params.investmentRange = investmentRange;
    }

    // Use bondsService instead of direct $fetch
    const res = await bondsService.getBondsListings(params);

    // Handle different response structures
    let listings = [];
    if (res && typeof res === 'object') {
      // Check if response has listings property
      if ('listings' in res && Array.isArray(res.listings)) {
        listings = res.listings.map(normalizeBondListing);
      } else if (Array.isArray(res)) {
        // If response is directly an array
        listings = res.map(normalizeBondListing);
      }
    }
    rawBondsListings.value = listings;
    const mapped = mapBondsToCardProps(listings);
    bondsWithMetadata.value = mapped;
    bondsCards.value = mapped.map((item) => item.card);
    filteredBondsCards.value = mapped.map((item) => item.card);
  } catch (err) {
    console.error("Failed to fetch bonds:", err);
    bondsCards.value = [];
    bondsWithMetadata.value = [];
    rawBondsListings.value = [];
    filteredBondsCards.value = [];
  } finally {
    bondsLoading.value = false;
  }
};

const fetchIpos = async () => {
  try {
    // const res = await ipoService.getIpoListings({ page: 1, limit: 20 });
    const res = { items: [] };
    const items = res?.items || [];
    ipoCards.value = items.map((ipo) => {
      const effective = Number(ipo.effectiveYieldPercentPa || 0);
      const coupon = Number(ipo.couponPercentPa || 0);
      const listed = Number(ipo.listedYield || 0);
      const rate = effective > 0 ? effective : (coupon > 0 ? coupon : listed);
      const minInvRaw = Number(ipo.minInvestment || 0);
      const minBidQty = Number(ipo.minBidQuantity || 0);
      const minPrice = Number(ipo.minPrice || 0);
      const minInvestment = minBidQty > 0 && minPrice > 0 ? minBidQty * minPrice : minInvRaw;
      return {
        id: ipo.id,
        issuerName: ipo.name || ipo.symbol || "IPO",
        rating: ipo.creditRating || "",
        ratingAgency: ipo.creditRatingAgency || "",
        couponRate: rate.toFixed(2),
        minAmount: minInvestment > 0 ? `₹${Math.round(minInvestment).toLocaleString("en-IN")}` : "N/A",
        tenure: ipo.tenureRange ? `${ipo.tenureRange} months` : "N/A",
        closingDate: ipo.biddingEndDate
          ? new Date(ipo.biddingEndDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
          : "N/A",
        tags: ipo.tags || [],
      };
    });
  } catch {
    ipoCards.value = [];
  }
};

const fetchFdSources = async () => {
  try {
    const res = await fetch("https://prod-api.equirize.com/fd-cb/fd/sources");
    const { sources } = await res.json();
    rawFdSources.value = sources || [];
    financeCards.value = mapToFinanceCards(sources, themeName.value === "dark");
  } catch (err) {
    console.error("Failed to fetch FD sources:", err);
    rawFdSources.value = [];
  }
};

// Handle tab change
const handleTabChange = (tab) => {
  activeTab.value = tab;
  if (tab === "bonds") {
    selectedChip.value = "highYield";
    fetchBonds(selectedChip.value);
  }
};

// Keyboard navigation for tabs
const tabOrder = ["bonds", "fds"];
const handleTabKeydown = (event, tabKey) => {
  const currentIndex = tabOrder.indexOf(tabKey);
  if (currentIndex === -1) return;

  switch (event.key) {
    case "ArrowRight": {
      event.preventDefault();
      const next = tabOrder[(currentIndex + 1) % tabOrder.length];
      document.getElementById(`tab-${next}`)?.focus();
      break;
    }
    case "ArrowLeft": {
      event.preventDefault();
      const prev = tabOrder[(currentIndex - 1 + tabOrder.length) % tabOrder.length];
      document.getElementById(`tab-${prev}`)?.focus();
      break;
    }
    case "Home": {
      event.preventDefault();
      document.getElementById(`tab-${tabOrder[0]}`)?.focus();
      break;
    }
    case "End": {
      event.preventDefault();
      document.getElementById(`tab-${tabOrder[tabOrder.length - 1]}`)?.focus();
      break;
    }
    case "Enter":
    case " ": {
      event.preventDefault();
      handleTabChange(tabKey);
      break;
    }
    default:
      break;
  }
};

// Keyboard navigation for bond filter tabs
const handleChipKeydown = (event, chipKey) => {
  const currentIndex = chipOrder.indexOf(chipKey);
  if (currentIndex === -1) return;

  switch (event.key) {
    case "ArrowRight": {
      event.preventDefault();
      const next = chipOrder[(currentIndex + 1) % chipOrder.length];
      document.getElementById(`bond-filter-tab-${next}`)?.focus();
      break;
    }
    case "ArrowLeft": {
      event.preventDefault();
      const prev = chipOrder[(currentIndex - 1 + chipOrder.length) % chipOrder.length];
      document.getElementById(`bond-filter-tab-${prev}`)?.focus();
      break;
    }
    case "Home": {
      event.preventDefault();
      document.getElementById(`bond-filter-tab-${chipOrder[0]}`)?.focus();
      break;
    }
    case "End": {
      event.preventDefault();
      document.getElementById(`bond-filter-tab-${chipOrder[chipOrder.length - 1]}`)?.focus();
      break;
    }
    case "Enter":
    case " ": {
      event.preventDefault();
      handleChipClick(chipKey);
      break;
    }
    default:
      break;
  }
};

// Handle chip click - fetch from API with filter
const handleChipClick = async (chipKey) => {
  selectedChip.value = chipKey;
  await fetchBonds(chipKey === "all" ? null : chipKey);
};

// Handle View All - redirect to invest page
const handleViewAll = () => {
  window.open(investorUrl("/login"), "_blank");
};

// fetch on mount
onMounted(async () => {
  if (props.showTabs) {
    await Promise.all([fetchBonds(selectedChip.value), fetchIpos(), fetchFdSources()]);
  } else {
    await fetchFdSources();
  }
});

const onKnowMore = (item) => {
  if (!item.card?.isin) return;
  navigateTo(getBondDetailsUrl(item.card.isin, item.card.name || item.card.issuerName));
};

const onCardClick = (item) => {
  if (!item.card?.isin) return;
  navigateTo(getBondDetailsUrl(item.card.isin, item.card.name || item.card.issuerName));
};
const onIpoClick = (ipo) => {
  navigateTo(getIpoDetailsUrl(ipo.id, ipo.issuerName));
};
// watch for theme switch to auto-update banners
watch(themeName, (newTheme) => {
  const isDark = newTheme === "dark";
  if (rawFdSources.value.length) {
    financeCards.value = mapToFinanceCards(rawFdSources.value, isDark);
  }
  if (rawBondsListings.value.length) {
    const mapped = mapBondsToCardProps(rawBondsListings.value);
    bondsWithMetadata.value = mapped;
    bondsCards.value = mapped.map((item) => item.card);
    filteredBondsCards.value = mapped.map((item) => item.card);
  }
});
</script>


<style scoped>
.products-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.subHeading {
  margin-top: 8px;
  color: var(--secondaryText, #666);
  text-align: center;
}

.tabs-container {
  display: flex;
  gap: 24px;
  margin-top: 24px;
  justify-content: center;
  align-items: center;
}

.tab {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  font-family: var(--font-family-primary);
}

.tab-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--secondaryText, #999);
  transition: color 0.3s ease;
}

.tab.active .tab-label {
  color: #0027AF;
  font-weight: 600;
}

.tab-count {
  font-size: 14px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  background: transparent;
  color: var(--secondaryText, #999);
  transition: all 0.3s ease;
}

.tab.active .tab-count {
  background: #E3F3FF;
  color: #0027AF;
}

.tab::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 2px;
  background: #0027AF;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.tab.active::after {
  transform: scaleX(1);
}

.chip-row {
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.chip {
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  padding: 6px 12px;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
  font-family: var(--font-family-primary);
  color: var(--primaryText);
  transition: all 0.3s ease;
}

.chip.active {
  border-color: #0027af;
  background: rgba(0, 39, 175, 0.08);
  color: #0027af;
}

.chip:hover {
  border-color: #0027af;
  background: rgba(0, 39, 175, 0.05);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  min-height: 300px;
  width: 100%;
}

.empty-state-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-state-title {
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 600;
  color: var(--primaryText);
  margin-bottom: 8px;
}

.empty-state-message {
  font-family: var(--font-family-primary);
  font-size: 14px;
  color: var(--secondaryText, #666);
  max-width: 500px;
  line-height: 1.5;
  margin-bottom: 24px;
}

.empty-state-button {
  padding: 10px 20px;
  border-radius: 6px;
  background: #0027AF;
  color: white;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: var(--font-family-primary);
}

.empty-state-button:hover {
  background: #001f8a;
  transform: translateY(-1px);
}

.cta-section {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  width: 100%;
  padding: 0 20px;
}

.cta-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 600px;
  width: 100%;
}

:deep(.cta-button button) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
  box-shadow: 0px 4px 12px rgba(0, 39, 175, 0.25) !important;
}

:deep(.cta-button button:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0px 6px 20px rgba(0, 39, 175, 0.4) !important;
  background-color: #001f8a !important;
}

@media (max-width: 639px) {
  .cta-content {
    padding: 24px 20px;
    gap: 14px;
  }

  .products-section {
    padding: 30px 10px;
  }

  .cta-section {
    margin-top: 40px;
    padding: 0 16px;
  }
}

.content {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  align-items: center;
}
#panel-fds{
 margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  align-items: center;
}

/* Bonds grid layout (similar to BondsWeOffer) */
.content .bonds-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  justify-content: center;
  align-items: flex-start;
}

.content .bonds-grid>* {
  flex: 0 0 auto;
  max-width: 450px;
  width: 100%;
}

.loading-state {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  justify-content: center;
}

.skeleton {
  width: 100%;
  max-width: 450px;
  height: 230px;
  border-radius: 7px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -400px 0;
  }

  100% {
    background-position: 400px 0;
  }
}

@media (min-width: 1024px) {
  .content {
    width: 80%;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 30px;
  }

  .content .bonds-grid {
    gap: 22px;
  }

  .content .bonds-grid>* {
    max-width: 450px;
    min-width: 300px;
  }
}

/* Tablet: 2 cards per row for bonds */
@media (min-width: 640px) and (max-width: 1023px) {
  .content .bonds-grid {
    gap: 18px;
  }

  .content .bonds-grid>* {
    flex: 0 1 calc(50% - 9px);
    max-width: 450px;
    min-width: 280px;
  }
}

/* Mobile: 1 card per row for bonds */
@media (max-width: 639px) {
  .content .bonds-grid {
    gap: 16px;
  }

  .content .bonds-grid>* {
    max-width: 100%;
    width: 100%;
  }
}
</style>
